<template>
  <div class="calc-logic-page">
    <!-- 顶部导航栏 -->
    <div class="page-topbar">
      <button class="back-btn" @click="handleBack">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        返回参数配置
      </button>
      <div class="topbar-title">计算逻辑</div>
    </div>

    <!-- 内容区 -->
    <div class="page-content">
      <!-- 加载状态 -->
      <div v-if="metaLoading" class="state-view">
        <i class="el-icon-loading" style="font-size:44px;color:#94a3b8;"></i>
        <span>正在加载计算逻辑...</span>
      </div>

      <!-- 数据加载完成 -->
      <template v-else-if="metadataData">
        <!-- 头部 -->
        <div class="page-header">
          <div>
            <h1 class="page-title">计算逻辑</h1>
            <p class="page-model-name">当前模型：{{ metadataData.modelName || activeTab }}</p>
          </div>
        </div>

        <!-- Tab 导航 -->
        <div class="tab-bar">
          <button class="tab-btn" :class="{ active: drawerTab === 'plain' }"
                  @click="setDrawerTab('plain')">通俗说明</button>
          <button class="tab-btn" :class="{ active: drawerTab === 'math' }" @click="setDrawerTab('math')">数学公式</button>
          <button class="tab-btn" :class="{ active: drawerTab === 'script' }"
                  @click="setDrawerTab('script')">技术脚本（高级）</button>
        </div>

        <!-- Tab 内容 -->
        <div class="tab-body">
          <!-- 通俗说明 -->
          <section v-if="drawerTab === 'plain'" class="logic-section">
            <div class="logic-box">
              <h3><span class="step-dot">1</span>核心思想</h3>
              <div class="markdown">{{ metadataData.coreIdea || '暂无核心思想' }}</div>
            </div>
            <div class="logic-box">
              <h3><span class="step-dot">2</span>计算流程</h3>
              <div class="markdown">{{ metadataData.calculationFlow || '暂无计算流程' }}</div>
            </div>
            <div class="logic-box">
              <h3><span class="step-dot">3</span>参数影响</h3>
              <div class="markdown">{{ metadataData.parameterImpact || '暂无参数影响' }}</div>
            </div>
          </section>

          <!-- 数学公式 -->
          <section v-if="drawerTab === 'math'" class="logic-section">
            <div class="logic-box math-scroll-box" ref="mathLogicBox" @scroll="handleMathLogicBoxScroll">
              <div class="markdown math-content" v-html="renderMath(metadataData.mathExplanation)"></div>
            </div>
          </section>

          <!-- 技术脚本 -->
          <section v-if="drawerTab === 'script'">
            <div v-if="metadataData.pythonScript" class="code-toolbar">
              <div>
                <b>Python 脚本示例</b>
                <div class="code-subtitle">可作为后端计算服务的参考实现</div>
              </div>
              <button class="copy-btn" @click="showScriptQr">下载脚本</button>
            </div>
            <pre v-if="metadataData.pythonScript" ref="codeBlock" class="code-block"
                 @scroll="handleCodeScroll"><code>{{ metadataData.pythonScript }}</code></pre>
            <div v-else class="logic-box">
              <div class="markdown dim">暂无脚本数据</div>
            </div>
          </section>
        </div>

      </template>

      <!-- 无数据 -->
      <div v-else class="state-view">
        <i class="el-icon-document" style="font-size:44px;color:#94a3b8;"></i>
        <span>暂无计算逻辑数据</span>
      </div>
    </div>

    <!-- 脚本二维码弹窗 -->
    <el-dialog title="Python 脚本" :visible.sync="scriptQrVisible" width="380px" :close-on-click-modal="false"
               class="qr-dialog" @closed="handleQrDialogClosed">
      <div class="qr-body">
        <p class="qr-tip">扫描下方二维码查看完整脚本</p>
        <div class="qr-code-wrapper">
          <!-- 二维码图片（始终占据空间，避免 CLS） -->
          <img v-if="scriptQrDataUrl" :src="scriptQrDataUrl" alt="脚本二维码" class="qr-code-img"
               :class="{ 'qr-img-visible': !qrLoading && scriptQrDataUrl }" @load="onQrImgLoad" @error="onQrImgError">
          <!-- 骨架屏（与二维码同尺寸同位置，交叉淡入淡出） -->
          <div v-if="showQrSkeleton" class="qr-skeleton" :class="{ 'qr-skeleton-fadeout': qrSkeletonFading }">
            <div class="qr-skeleton-grid">
              <span v-for="n in 9" :key="n" class="qr-skeleton-cell"></span>
            </div>
            <div class="qr-skeleton-spinner">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </div>
            <p class="qr-skeleton-hint">二维码生成中</p>
          </div>
          <!-- 空状态占位 -->
          <svg v-if="!qrLoading && !scriptQrDataUrl" class="qr-placeholder-icon" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
      </div>
      <span slot="footer" class="qr-footer">
        <el-button @click="scriptQrVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import apiOpen from '@/api/open'
import { httpUpload } from '@/utils/requestEx'
import QRCode from 'qrcode'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

export default {
  name: 'CalcLogic',
  data () {
    return {
      metaLoading: false,
      metadataData: null,
      drawerTab: 'plain',
      scriptCopied: false,
      scriptQrVisible: false,
      scriptQrCodeUrl: '',
      scriptQrDataUrl: '',
      qrLoading: false,
      showQrSkeleton: false,
      qrSkeletonFading: false,
      qrSkeletonTimer: null,
      codeScrollTimer: null,
      remoteApplyTime: 0,
      lastSentCodeRatio: -1,
      lastSentMathRatio: -1
    }
  },
  computed: {
    activeTab () {
      return localStorage.getItem('activeModelTab') || this.$route.query.model || 'MeanVariance'
    },
    drawerTabKeys () {
      return ['plain', 'math', 'script']
    },
    prevDrawerTab () {
      const keys = ['plain', 'math', 'script']
      const idx = keys.indexOf(this.drawerTab)
      return keys[(idx - 1 + keys.length) % keys.length]
    },
    nextDrawerTab () {
      const keys = ['plain', 'math', 'script']
      const idx = keys.indexOf(this.drawerTab)
      return keys[(idx + 1) % keys.length]
    }
  },
  watch: {
    scriptQrCodeUrl (newVal) {
      if (newVal) {
        this.generateScriptQr(newVal)
      }
    }
  },
  created () {
    this.syncModelToStorage()
    this.loadData()
    const savedTab = localStorage.getItem('calcLogicDrawerTab')
    if (savedTab && ['plain', 'math', 'script'].includes(savedTab)) {
      this.drawerTab = savedTab
    }
  },
  mounted () {
    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy () {
    window.removeEventListener('storage', this.handleStorageChange)
    localStorage.removeItem('calcLogicDrawerTab')
    localStorage.removeItem('calcLogicCodeScroll')
    localStorage.removeItem('calcLogicTabBodyScroll')
    localStorage.removeItem('calcLogicMathScroll')
    if (this.qrSkeletonTimer) {
      clearTimeout(this.qrSkeletonTimer)
      this.qrSkeletonTimer = null
    }
    if (this.codeScrollTimer) {
      clearTimeout(this.codeScrollTimer)
      this.codeScrollTimer = null
    }
  },
  methods: {
    syncModelToStorage () {
      const model = this.activeTab
      const prev = localStorage.getItem('activeModelTab')
      if (prev !== model) {
        localStorage.setItem('activeModelTab', model)
      }
    },
    handleStorageChange (event) {
      if (event.key === 'activeModelTab' && event.newValue !== this.activeTab) {
        this.$router.replace({ query: { model: event.newValue } })
        this.loadData(event.newValue)
      } else if (event.key === 'calcLogicDrawerTab' && event.newValue !== this.drawerTab) {
        this.setDrawerTab(event.newValue)
      } else if (event.key === 'calcLogicCodeScroll') {
        // 副屏代码滚动同步，按比例适配本屏高度
        if (this.$refs.codeBlock && this.drawerTab === 'script') {
          this.restoreCodeScroll()
        }
      } else if (event.key === 'calcLogicMathScroll') {
        if (this.$refs.mathLogicBox && this.drawerTab === 'math') {
          this.restoreMathLogicBoxScroll()
        }
      } else if (event.key === 'calcLogicScriptQr' && event.newValue) {
        // 副屏收到主屏的脚本二维码事件
        try {
          const data = JSON.parse(event.newValue)
          if (data && data.closed) {
            // 主屏关闭弹窗 → 副屏也关闭
            this.scriptQrVisible = false
            this.scriptQrCodeUrl = ''
            this.qrLoading = false
            this.showQrSkeleton = false
            this.qrSkeletonFading = false
            if (this.qrSkeletonTimer) { clearTimeout(this.qrSkeletonTimer); this.qrSkeletonTimer = null }
          } else if (data && data.action === 'start') {
            // 主屏开始生成 → 副屏显示骨架屏
            this.scriptQrVisible = true
            this.scriptQrCodeUrl = ''
            this.qrLoading = true
            this.showQrSkeleton = true
            this.qrSkeletonFading = false
          } else if (data && data.url) {
            // 副屏收到主屏上传完成的 URL，设置后 watch 自动生成本地二维码
            this.scriptQrCodeUrl = data.url
          }
        } catch (e) { /* ignore */ }
      }
    },
    loadData (modelKey) {
      this.metaLoading = true
      this.metadataData = null
      this.drawerTab = 'plain'

      const key = modelKey || this.activeTab

      const modelMap = {
        MeanVariance: 1,
        RiskParity: 2,
        MinVariance: 3,
        BlackLitterman: 4,
        EqualWeight: 5
      }

      apiOpen.ModelMetadata({ model: modelMap[key] || 1 }).then(res => {
        console.log('ModelMetadata 返回结果：', res)
        this.metadataData = res?.data || res
        this.metaLoading = false
        this.$nextTick(() => {
          this.loadMathJax().then(() => {
            this.typesetMath()
          }).catch(() => {
            console.warn('MathJax 加载失败，公式将以文本形式展示')
          })
        })
      }).catch(err => {
        console.error('ModelMetadata 请求失败：', err)
        this.metaLoading = false
        this.$message.error('获取计算逻辑失败')
      })
    },
    setDrawerTab (tab) {
      this.drawerTab = tab
      this.scriptCopied = false
      localStorage.setItem('calcLogicDrawerTab', tab)
      if (tab === 'math') {
        this.$nextTick(() => {
          if (window.MathJax && window.MathJax.typesetPromise) {
            window.MathJax.typesetPromise()
          }
          this.restoreMathLogicBoxScroll()
        })
      }
      if (tab === 'script') {
        this.$nextTick(this.restoreCodeScroll)
      }
    },
    handleCodeScroll () {
      const el = this.$refs.codeBlock
      if (!el) return
      // 时间戳防抖：刚应用远程滚动 200ms 内不写入，阻断反馈循环
      if (Date.now() - this.remoteApplyTime < 200) return
      const percent = el.scrollHeight > el.clientHeight
        ? el.scrollTop / (el.scrollHeight - el.clientHeight)
        : 0
      // 跳过重复比例，避免无意义写入
      if (Math.abs(percent - this.lastSentCodeRatio) < 0.002) return
      this.lastSentCodeRatio = percent
      if (this.codeScrollTimer) clearTimeout(this.codeScrollTimer)
      this.codeScrollTimer = setTimeout(() => {
        localStorage.setItem('calcLogicCodeScroll', String(percent))
        this.codeScrollTimer = null
      }, 150)
    },
    restoreCodeScroll () {
      this.remoteApplyTime = Date.now()
      const saved = parseFloat(localStorage.getItem('calcLogicCodeScroll'))
      if (isNaN(saved)) return
      const el = this.$refs.codeBlock
      if (!el) return
      this.$nextTick(() => {
        const maxScroll = el.scrollHeight - el.clientHeight
        if (maxScroll > 0) {
          el.scrollTop = saved * maxScroll
        }
      })
    },
    //测试音频是否被屏蔽
    checkAudioStatus () {
      const audios = document.querySelectorAll('audio');
      if (audios.length === 0) {
        console.log('✅ 检测结果：页面中没有任何 <audio> 元素（或已被完全移除）');
        return;
      }
      let blockedCount = 0;
      audios.forEach((audio, index) => {
        const isMuted = audio.muted;
        const isVolZero = audio.volume === 0;
        const isPaused = audio.paused;
        const hasError = audio.error !== null;
        const isHidden = audio.offsetParent === null;
        const hasSrc = audio.src || audio.currentSrc;
        // 判定为“被屏蔽”的标准（综合判断）
        const isBlocked = isMuted || isVolZero || hasError || !hasSrc;
        // 记录详情
        console.log(`🎵 音频 #${index}:`, {
          src: audio.src || '无',
          muted: isMuted,
          volume: audio.volume,
          paused: isPaused,
          hasError: hasError,
          errorCode: audio.error?.code || '无',
          isHidden: isHidden,
          isBlocked: isBlocked
        });
        if (isBlocked) blockedCount++;
      });
      console.log(`📊 总计: ${audios.length} 个音频，其中 ${blockedCount} 个处于屏蔽/静音/错误状态`);
    },
    showScriptQr () {
      // 屏蔽所有语音
      // this.stopAllAudio()
      document.querySelectorAll('audio').forEach(a => { a.pause(); a.src = ''; a.remove() })
      this.checkAudioStatus()

      if (!this.metadataData?.pythonScript) return
      this.qrError = false
      this.qrErrorMessage = ''
      this.scriptQrCodeUrl = ''
      this.scriptQrVisible = true
      this.qrLoading = true
      this.showQrSkeleton = true
      this.qrSkeletonFading = false
      this.qrLoadStatus = '正在准备渲染脚本...'
      // 同步副屏：开始生成，显示骨架屏
      localStorage.setItem('calcLogicScriptQr', JSON.stringify({ action: 'start', timestamp: Date.now() }))
      this.generateScriptImage()
    },
    generateScriptImage () {
      const container = document.createElement('div')
      container.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;padding:28px;background:#1e293b;font-family:Consolas,Monaco,monospace;'
      const pre = document.createElement('pre')
      pre.style.cssText = 'color:#e2e8f0;font-size:13px;line-height:1.65;white-space:pre-wrap;word-break:break-all;margin:0;'
      pre.textContent = this.metadataData.pythonScript
      container.appendChild(pre)
      document.body.appendChild(container)

      html2canvas(container, { scale: 1, backgroundColor: '#1e293b' }).then(canvas => {
        document.body.removeChild(container)
        const pdf = new jsPDF('p', 'mm', 'a4')
        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        // 用 JPEG 压缩，大幅减小体积（代码文本不需要 PNG 无损）
        const imgData = canvas.toDataURL('image/jpeg', 0.8)
        const imgWidth = pageWidth - 20
        const imgHeight = (canvas.height * imgWidth) / canvas.width
        const marginLeft = 10
        const marginTop = 10
        let heightLeft = imgHeight
        let position = 0

        pdf.addImage(imgData, 'JPEG', marginLeft, marginTop, imgWidth, imgHeight)
        heightLeft -= (pageHeight - 20)

        while (heightLeft > 0) {
          position = heightLeft - imgHeight
          pdf.addPage()
          pdf.addImage(imgData, 'JPEG', marginLeft, position + marginTop, imgWidth, imgHeight)
          heightLeft -= (pageHeight - 20)
        }

        const pdfBlob = new Blob([pdf.output('arraybuffer')], { type: 'application/pdf' })
        const formData = new FormData()
        formData.append('file', pdfBlob, 'python_script.pdf')
        httpUpload('/api/open/portfolio/reports/upload', formData).then(res => {
          const url = (res?.data?.url || res?.url || '').trim()
          if (url) {
            this.scriptQrCodeUrl = url
            // 同步到副屏：写入 localStorage 触发 storage 事件
            localStorage.setItem('calcLogicScriptQr', JSON.stringify({ url, timestamp: Date.now() }))
          } else {
            this.$message.warning('PDF上传成功但未获取到链接')
            this.scriptQrVisible = false
          }
        }).catch(err => {
          console.error('PDF上传失败：', err)
          this.$message.error('PDF上传失败')
          this.scriptQrVisible = false
        })
      }).catch(err => {
        document.body.removeChild(container)
        console.error('脚本截图失败：', err)
        this.$message.error('脚本截图失败')
        this.scriptQrVisible = false
      })
    },
    onQrImgLoad () {
      // 先触发骨架屏淡出
      this.qrSkeletonFading = true
      this.qrLoading = false
      // 骨架屏淡出动画完成后（0.4s）移除 DOM，避免 CLS
      if (this.qrSkeletonTimer) clearTimeout(this.qrSkeletonTimer)
      this.qrSkeletonTimer = setTimeout(() => {
        this.showQrSkeleton = false
        this.qrSkeletonFading = false
        this.qrSkeletonTimer = null
      }, 420)
    },
    onQrImgError () {
      this.qrLoading = false
      this.showQrSkeleton = false
      this.qrSkeletonFading = false
      if (this.qrSkeletonTimer) { clearTimeout(this.qrSkeletonTimer); this.qrSkeletonTimer = null }
      this.$message.error('二维码图片加载失败，请重试')
    },
    handleQrDialogClosed () {
      this.scriptQrCodeUrl = ''
      this.scriptQrDataUrl = ''
      this.qrLoading = false
      this.showQrSkeleton = false
      this.qrSkeletonFading = false
      if (this.qrSkeletonTimer) { clearTimeout(this.qrSkeletonTimer); this.qrSkeletonTimer = null }
      // 通知副屏关闭弹窗
      localStorage.setItem('calcLogicScriptQr', JSON.stringify({ closed: true, timestamp: Date.now() }))
    },
    generateScriptQr (url) {
      QRCode.toDataURL(url, { width: 240, margin: 1 })
        .then(dataUrl => {
          this.scriptQrDataUrl = dataUrl
          this.qrLoading = false
          if (this.qrSkeletonTimer) clearTimeout(this.qrSkeletonTimer)
          this.qrSkeletonFading = true
          this.qrSkeletonTimer = setTimeout(() => {
            this.showQrSkeleton = false
            this.qrSkeletonFading = false
            this.qrSkeletonTimer = null
          }, 420)
        })
        .catch(() => {
          this.qrLoading = false
          this.showQrSkeleton = false
          this.qrSkeletonFading = false
        })
    },
    handleMathLogicBoxScroll () {
      const el = this.$refs.mathLogicBox
      if (!el) return
      // 时间戳防抖：刚应用远程滚动 200ms 内不写入，阻断反馈循环
      if (Date.now() - this.remoteApplyTime < 200) return
      const percent = el.scrollHeight > el.clientHeight
        ? el.scrollTop / (el.scrollHeight - el.clientHeight)
        : 0
      // 跳过重复比例，避免无意义写入
      if (Math.abs(percent - this.lastSentMathRatio) < 0.002) return
      this.lastSentMathRatio = percent
      if (this.codeScrollTimer) clearTimeout(this.codeScrollTimer)
      this.codeScrollTimer = setTimeout(() => {
        localStorage.setItem('calcLogicMathScroll', String(percent))
        this.codeScrollTimer = null
      }, 150)
    },
    restoreMathLogicBoxScroll () {
      this.remoteApplyTime = Date.now()
      const saved = parseFloat(localStorage.getItem('calcLogicMathScroll'))
      if (isNaN(saved)) return
      const el = this.$refs.mathLogicBox
      if (!el) return
      const maxScroll = el.scrollHeight - el.clientHeight
      if (maxScroll > 0) {
        el.scrollTop = saved * maxScroll
      }
    },
    renderMath (text) {
      if (!text) return ''
      let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      const blocks = []
      html = html.replace(/\$\$(.+?)\$\$/gs, (match) => {
        blocks.push(match)
        return `%%MATHBLOCK_${blocks.length - 1}%%`
      })
      const inlines = []
      html = html.replace(/\$(.+?)\$/gs, (match) => {
        if (match.startsWith('%%MATHBLOCK_')) return match
        inlines.push(match)
        return `%%MATHINLINE_${inlines.length - 1}%%`
      })
      html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      html = html.replace(/\n{2,}/g, '</p><p>')
      html = html.replace(/\n/g, '<br>')
      html = '<p>' + html + '</p>'
      html = html.replace(/%%MATHBLOCK_(\d+)%%/g, (_, i) => {
        return '<div class="formula">' + blocks[+i] + '</div>'
      })
      html = html.replace(/%%MATHINLINE_(\d+)%%/g, (_, i) => {
        return inlines[+i]
      })
      return html
    },
    loadMathJax () {
      if (window.MathJax && window.MathJax.typesetPromise) {
        return Promise.resolve()
      }
      if (!window.MathJax) {
        window.MathJax = {
          tex: {
            inlineMath: [['$', '$']],
            displayMath: [['$$', '$$']]
          },
          options: {
            enableMenu: false
          }
        }
      }
      return new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-chtml.js'
        script.async = true
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
      })
    },
    typesetMath () {
      if (window.MathJax && window.MathJax.typesetPromise) {
        this.$nextTick(() => {
          window.MathJax.typesetPromise()
        })
      }
    },
    handleBack () {
      this.$router.push({ name: 'Aimoxingyucanshu' })
    }
  }
}
</script>

<style scoped lang="less">
.calc-logic-page {
  min-height: 100vh;
  // background: linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 50%, #f5f0ff 100%);
  background: url('../../assets/bg-hp.png') no-repeat;
  background-size: 100% 100%;
  display: flex;
  flex-direction: column;
}

/* ===== 顶部导航 ===== */
.page-topbar {
  display: flex;
  align-items: center;
  padding: 16px 32px;
  background: white;
  border-bottom: 1px solid #e8eef7;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background 0.2s;
}

.back-btn:hover {
  background: #eff6ff;
}

.topbar-title {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

/* ===== 内容主体 ===== */
.page-content {
  flex: 1;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 32px 32px;
  display: flex;
  flex-direction: column;
}

/* 加载 & 空状态 */
.state-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 100px 0;
  color: #94a3b8;
  font-size: 15px;
}

/* ===== 头部 ===== */
.page-header {
  padding-bottom: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #172033;
  margin: 0;
}

.page-model-name {
  font-size: 13px;
  color: #6a7891;
  margin: 6px 0 0;
}

/* ===== Tab 导航 ===== */
.tab-bar {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  padding-bottom: 16px;
}

.tab-bar .tab-btn {
  height: 42px;
  border: 1px solid #d9e3f3;
  background: #f7faff;
  color: #536177;
  font-weight: 800;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
  position: relative;
}

.tab-bar .tab-btn:first-child {
  border-radius: 10px 0 0 10px;
}

.tab-bar .tab-btn:last-child {
  border-radius: 0 10px 10px 0;
}

.tab-bar .tab-btn+.tab-btn {
  margin-left: -1px;
}

.tab-bar .tab-btn.active {
  color: #236cff;
  background: #fff;
  border-color: #236cff;
  box-shadow: 0 8px 18px rgba(35, 108, 255, 0.12);
  z-index: 1;
}

.tab-bar .tab-btn:not(.active):hover {
  background: #fff;
  color: #3b82f6;
}

/* ===== 内容区 ===== */
.tab-body {
  flex: 1;
  overflow-y: auto;
}

/* ===== 逻辑卡片 ===== */
.logic-section {
  display: grid;
  gap: 14px;
}

.logic-box {
  padding: 18px;
  border: 1px solid #e5edf8;
  background: #fbfdff;
  border-radius: 16px;
  transition: box-shadow 0.2s ease;
}

.logic-box:hover {
  box-shadow: 0 2px 12px rgba(35, 108, 255, 0.06);
}

/* 数学公式 tab 的 logic-box：固定高度 + 内部滚动 */
.logic-section .logic-box.math-scroll-box {
  overflow-y: auto;
  max-height: 550px;
}

@media screen and (max-height: 800px) {
  .logic-section .logic-box.math-scroll-box {
    max-height: 550px;
  }
}

@media screen and (min-height: 1080px) {
  .logic-section .logic-box.math-scroll-box {
    max-height: 1000px;
  }
}

.logic-box h3 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #172033;
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-dot {
  width: 24px;
  height: 24px;
  display: inline-grid;
  place-items: center;
  border-radius: 50%;
  background: #236cff;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-dot:empty {
  display: none;
}

.markdown {
  line-height: 1.85;
  color: #334155;
  font-size: 14px;
}

.markdown b,
.markdown strong {
  color: #172033;
}

.markdown.dim {
  color: #94a3b8;
  font-style: italic;
}

/* ===== 公式 ===== */
.formula {
  padding: 14px 16px;
  margin: 12px 0;
  border-radius: 12px;
  background: #f5f8ff;
  border: 1px solid #dfeaff;
  text-align: center;
  overflow-x: auto;
}

.formula mjx-container {
  font-size: 110%;
}

.math-content {
  line-height: 1.8;
}

.math-content mjx-container[display="inline"] {
  font-size: 100%;
  display: inline !important;
}

.math-content mjx-container[display="block"] {
  margin: 8px 0;
}

/* ===== 代码区域 ===== */
.code-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 12px;
}

.code-toolbar b {
  font-size: 14px;
  color: #172033;
}

.code-subtitle {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.copy-btn {
  border: 1px solid #c9d8f5;
  background: #fff;
  color: #236cff;
  border-radius: 10px;
  padding: 8px 14px;
  cursor: pointer;
  font-weight: 800;
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  background: #f0f6ff;
  border-color: #236cff;
}

.code-block {
  margin: 0;
  padding: 18px;
  border-radius: 14px;
  background: #0f172a;
  color: #dbeafe;
  overflow: auto;
  font-size: 13px;
  line-height: 1.65;
  max-height: 480px;
  font-family: 'Courier New', Consolas, monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

/* 小屏笔记本 */
@media screen and (max-height: 800px) {
  .code-block {
    max-height: 500px;
  }
}

/* 大屏 */
@media screen and (min-height: 1080px) {
  .code-block {
    max-height: 1000px;
  }
}

/* ===== 底部导航 ===== */
.page-footer {
  padding: 16px 0 0;
  border-top: 1px solid #e8eef7;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
}

.page-footer .btn {
  flex: 1;
  height: 44px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.page-footer .btn.primary {
  color: #fff;
  background: linear-gradient(135deg, #176dff, #604dff);
  box-shadow: 0 8px 18px rgba(45, 119, 255, 0.24);
}

.page-footer .btn.primary:hover {
  box-shadow: 0 10px 24px rgba(45, 119, 255, 0.32);
  transform: translateY(-1px);
}

.page-footer .btn.secondary {
  background: #fff;
  color: #236cff;
  border-color: #bdd1ff;
}

.page-footer .btn.secondary:hover {
  background: #f0f6ff;
  border-color: #236cff;
}
</style>

<style lang="less">
/* ===== 脚本二维码弹窗样式（非 scoped） ===== */
.qr-dialog .qr-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.qr-dialog .qr-tip {
  font-size: 14px;
  color: #475569;
  margin: 0;
  transition: color 0.3s;
}

/* ===== 二维码容器 ===== */
.qr-dialog .qr-code-wrapper {
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  position: relative;
}

/* ===== 二维码图片 ===== */
.qr-dialog .qr-code-img {
  width: 200px;
  height: 200px;
  opacity: 0;
  transition: opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
}

.qr-dialog .qr-code-img.qr-img-visible {
  opacity: 1;
}

/* ===== 骨架屏 ===== */
.qr-dialog .qr-skeleton {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f1f5f9;
  border-radius: 8px;
  overflow: hidden;
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
}

/* 骨架屏退出动画 */
.qr-dialog .qr-skeleton.qr-skeleton-fadeout {
  opacity: 0;
  pointer-events: none;
}

/* 光泽扫过动画 */
.qr-dialog .qr-skeleton::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.5) 40%,
      rgba(255, 255, 255, 0.7) 50%,
      rgba(255, 255, 255, 0.5) 60%,
      transparent 100%);
  animation: qr-shimmer 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes qr-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* 伪二维码网格 */
.qr-dialog .qr-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 5px;
  width: 90px;
  height: 90px;
  opacity: 0.5;
}

.qr-dialog .qr-skeleton-cell {
  background: #cbd5e1;
  border-radius: 3px;
  animation: qr-cell-pulse 2s ease-in-out infinite;
}

.qr-dialog .qr-skeleton-cell:nth-child(1) {
  animation-delay: 0s;
}

.qr-dialog .qr-skeleton-cell:nth-child(2) {
  animation-delay: 0.15s;
}

.qr-dialog .qr-skeleton-cell:nth-child(3) {
  animation-delay: 0.3s;
}

.qr-dialog .qr-skeleton-cell:nth-child(4) {
  animation-delay: 0.45s;
}

.qr-dialog .qr-skeleton-cell:nth-child(5) {
  animation-delay: 0.6s;
}

.qr-dialog .qr-skeleton-cell:nth-child(6) {
  animation-delay: 0.75s;
}

.qr-dialog .qr-skeleton-cell:nth-child(7) {
  animation-delay: 0.9s;
}

.qr-dialog .qr-skeleton-cell:nth-child(8) {
  animation-delay: 1.05s;
}

.qr-dialog .qr-skeleton-cell:nth-child(9) {
  animation-delay: 1.2s;
}

@keyframes qr-cell-pulse {

  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.95);
  }

  50% {
    opacity: 0.9;
    transform: scale(1);
  }
}

/* 骨架屏旋转图标 */
.qr-dialog .qr-skeleton-spinner {
  width: 24px;
  height: 24px;
  color: #3b82f6;
  animation: qr-spin 1s linear infinite;
}

@keyframes qr-spin {
  to {
    transform: rotate(360deg);
  }
}

/* 骨架屏提示文字 */
.qr-dialog .qr-skeleton-hint {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
  letter-spacing: 0.02em;
}

/* ===== 空状态占位 ===== */
.qr-dialog .qr-placeholder-icon {
  width: 64px;
  height: 64px;
  color: #cbd5e1;
  opacity: 0.5;
}

.qr-dialog .qr-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ===== 竖屏适配 ===== */
@media screen and (orientation: portrait) {
  .qr-dialog {
    width: 100vw !important;
  }

  .qr-dialog .qr-code-wrapper {
    width: 180px;
    height: 180px;
  }

  .qr-dialog .qr-code-img {
    width: 160px;
    height: 160px;
  }

  .qr-dialog .qr-skeleton {
    width: 160px;
    height: 160px;
  }

  .qr-dialog .qr-skeleton-grid {
    width: 72px;
    height: 72px;
    gap: 4px;
  }

  .qr-dialog .qr-tip {
    font-size: 13px;
  }
}
</style>
